import { useQuery } from '@tanstack/react-query'
import { PieChart, PieSlice, PieCenter } from '@/components/charts'
import { useAppStore } from '@/store'
import { fetchCofogExpenditures } from '@/data/eurostat'
import { useNavigate } from 'react-router'

// Distinct colors — one per function
const COLORS: Record<string, string> = {
  GF01: '#636e72', // Services généraux — gris
  GF02: '#2d3436', // Défense — noir
  GF03: '#74b9ff', // Ordre public — bleu clair
  GF04: '#00cec9', // Affaires éco — turquoise
  GF05: '#55efc4', // Environnement — vert menthe
  GF06: '#fdcb6e', // Logement — jaune
  GF07: '#ff7675', // Santé — corail
  GF08: '#a29bfe', // Culture — lavande
  GF09: '#0984e3', // Enseignement — bleu
  GF10: '#e17055', // Protection sociale — orange
}

export default function DepensesByFunction() {
  const { selectedYear } = useAppStore()
  const navigate = useNavigate()

  const expQuery = useQuery({
    queryKey: ['cofog', selectedYear],
    queryFn: () => fetchCofogExpenditures(selectedYear),
    staleTime: 1000 * 60 * 60,
  })

  const expenditures = expQuery.data ?? []
  const total = expenditures.reduce((s, e) => s + e.value, 0)

  // Flatten expenditure items into pie data
  const pieData = expenditures.map((e) => ({
    label: e.label,
    value: Math.round(e.value),
    color: COLORS[e.code] ?? '#ccc',
    code: e.code,
  }))

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold">Dépenses publiques — {selectedYear}</h2>
        <p className="text-text-muted mt-1">
          Dépenses de consommation finale par fonction COFOG (S13)
        </p>
      </div>

      {expQuery.isLoading && (
        <div className="bg-card rounded-lg shadow p-8 text-center text-text-muted">
          Chargement des données Eurostat…
        </div>
      )}

      {expenditures.length > 0 && (
        <>
          <div className="bg-card rounded-lg shadow p-6 mb-6">
            <div className="flex items-baseline gap-4">
              <span className="text-text-muted">Total dépenses :</span>
              <span className="text-4xl font-bold text-nav">
                {(total / 1000).toFixed(0)} Md€
              </span>
              <span className="text-text-muted text-sm">consommation finale APU</span>
            </div>
          </div>

          <div className="bg-card rounded-lg shadow p-6 mb-6">
            <h3 className="font-semibold text-lg mb-4">Répartition par fonction (COFOG)</h3>
            <div className="flex justify-center">
              <PieChart data={pieData} size={420} innerRadius={90}>
                {pieData.map((item, i) => (
                  <PieSlice
                    key={item.code}
                    index={i}
                    color={item.color}
                  />
                ))}
                <PieCenter defaultLabel="Total" />
              </PieChart>
            </div>
          </div>

          <div className="bg-card rounded-lg shadow p-6">
            <h3 className="font-semibold text-lg mb-4">Détail par fonction</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-text-muted">
                  <th className="pb-2">Fonction</th>
                  <th className="pb-2 text-right">Montant</th>
                  <th className="pb-2 text-right">% du total</th>
                  <th className="pb-2 text-right w-32">Répartition</th>
                </tr>
              </thead>
              <tbody>
                {expenditures
                  .sort((a, b) => b.value - a.value)
                  .map((exp) => (
                    <tr
                      key={exp.code}
                      className="border-b border-border hover:bg-muted cursor-pointer"
                      onClick={() => navigate(`/detail/cofog/${exp.code}`)}
                    >
                      <td className="py-3 flex items-center gap-2">
                        <span
                          className="w-3 h-3 rounded-full inline-block"
                          style={{ backgroundColor: COLORS[exp.code] ?? '#ccc' }}
                        />
                        {exp.label}
                      </td>
                      <td className="py-3 text-right font-semibold">
                        {(exp.value / 1000).toFixed(0)} Md€
                      </td>
                      <td className="py-3 text-right">{exp.pctOfTotal.toFixed(1)}%</td>
                      <td className="py-3 text-right">
                        <div className="inline-block w-24 bg-muted rounded-full h-2">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${exp.pctOfTotal}%`,
                              backgroundColor: COLORS[exp.code] ?? '#ccc',
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <p className="text-xs text-text-muted mt-4">
        Source : Eurostat gov_10a_exp (P3 — consommation finale, M€, S13)
      </p>
    </div>
  )
}
